import { addDays, format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Divider } from 'primereact/divider';
import React, { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { centsToFormattedDollars } from '../../common/utils/formatting';
import { useGetIctTransfers } from '../hooks/useIctData';
import type {
    AdminICTTransferDetailDto,
    IctStatus,
    NetWireDirection,
} from '../types/ImOpsTypes';
import { ICT_ERROR_STATUSES, ICT_PENDING_STATUSES } from '../types/ImOpsTypes';
import { ImOpsDailyReconPanel } from './ImOpsDailyRecon';
import { TransferDetailsModal } from './TransferDetailsModal';

const BANK_TIME_ZONE = 'America/New_York';

const ICT_TRANSFERS_DATATABLE_STYLE: React.CSSProperties &
    Record<'--deposit-bg' | '--withdrawal-bg', string> = {
    '--deposit-bg': '#e8f5e9',
    '--withdrawal-bg': '#fff3e0',
};

const getStatusColor = (status: IctStatus): string => {
    switch (status) {
        case 'COMPLETED':
            return '#28a745'; // green
        case 'INITIATED':
        case 'WAITING_COMPLETED':
            return '#ffc107'; // yellow/amber
        case 'FAILED':
        case 'FAILED_INCONSISTENT':
            return '#dc3545'; // red
        case 'CANCELLED':
            return '#6c757d'; // gray
        default:
            return '#000000';
    }
};

// Helper function to get wire direction text
const getWireDirectionText = (wireDirection: NetWireDirection): string => {
    switch (wireDirection) {
        case 'WIRE_EB_TO_APEX':
            return 'Wire EB → Apex';
        case 'WIRE_APEX_TO_EB':
            return 'Wire Apex → EB';
        case 'NO_WIRE':
            return 'No Wire (Balanced)';
        default:
            return 'Unknown';
    }
};

const isPending = (t: AdminICTTransferDetailDto) =>
    ICT_PENDING_STATUSES.includes(t.status);
const isError = (t: AdminICTTransferDetailDto) =>
    ICT_ERROR_STATUSES.includes(t.status);

// Generate ICT summary data from API response
const generateIctSummaryFromApi = (
    deposits: AdminICTTransferDetailDto[],
    withdrawals: AdminICTTransferDetailDto[],
) => {
    const depositComplete = deposits.filter(
        (t) => t.status === 'COMPLETED',
    ).length;
    const depositPending = deposits.filter(isPending).length;
    const depositError = deposits.filter(isError).length;
    const depositCancelled = deposits.filter(
        (t) => t.status === 'CANCELLED',
    ).length;

    const withdrawalComplete = withdrawals.filter(
        (t) => t.status === 'COMPLETED',
    ).length;
    const withdrawalPending = withdrawals.filter(isPending).length;
    const withdrawalError = withdrawals.filter(isError).length;
    const withdrawalCancelled = withdrawals.filter(
        (t) => t.status === 'CANCELLED',
    ).length;

    const depositSum = deposits.reduce(
        (sum, t) => sum + (t.amountCents ?? 0),
        0,
    );
    const withdrawalSum = withdrawals.reduce(
        (sum, t) => sum + (t.amountCents ?? 0),
        0,
    );
    const withdrawalTaxWithheldSum = withdrawals.reduce(
        (sum, t) => sum + (t.taxAmountWithheldCents ?? 0),
        0,
    );

    return [
        {
            type: 'ICT Deposit',
            numTrx: deposits.length,
            amountCents: depositSum,
            taxAmountWithheldCents: null,
            numComplete: depositComplete,
            numPending: depositPending,
            numError: depositError,
            numCancel: depositCancelled,
        },
        {
            type: 'ICT Withdrawal',
            numTrx: withdrawals.length,
            amountCents: withdrawalSum,
            taxAmountWithheldCents: withdrawalTaxWithheldSum,
            numComplete: withdrawalComplete,
            numPending: withdrawalPending,
            numError: withdrawalError,
            numCancel: withdrawalCancelled,
        },
    ];
};

type IctSummaryRow = ReturnType<typeof generateIctSummaryFromApi>[number];

export const ImOpsIctDailyPanel = () => {
    // Get today's date in ET timezone
    const today = new Date();
    const [settlementDate, setSettlementDate] = useState<Date>(today);
    const [selectedTransfer, setSelectedTransfer] =
        useState<AdminICTTransferDetailDto | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Allow selecting up to 5 days in the future to account for weekends and holidays
    const maxSelectableDate = addDays(today, 5);

    // Format date in ET timezone for API (YYYY-MM-DD)
    const settlementDateString = formatInTimeZone(
        settlementDate,
        BANK_TIME_ZONE,
        'yyyy-MM-dd',
    );

    // Fetch ICT transfers from API
    const { data, isLoading, error, refetch } = useGetIctTransfers({
        settlementDate: settlementDateString,
    });

    const handleDateChange = (value: Date | null | undefined): void => {
        if (value) {
            setSettlementDate(value);
        }
    };

    const handleRowClick = (rowData: AdminICTTransferDetailDto) => {
        setSelectedTransfer(rowData);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTransfer(null);
    };

    const handleCancelSuccess = () => {
        // Refresh the transfers data after successful cancellation
        void refetch();
    };

    const statusBodyTemplate = (rowData: AdminICTTransferDetailDto) => {
        return (
            <BodySmallEmphasized color={getStatusColor(rowData.status)}>
                {rowData.status}
            </BodySmallEmphasized>
        );
    };

    const apexDetailsBodyTemplate = (rowData: AdminICTTransferDetailDto) => {
        return (
            <YStack>
                <BodySmallRegular>
                    <BodySmallEmphasized>ID:</BodySmallEmphasized>{' '}
                    {rowData.apexIctId ?? '-'}
                </BodySmallRegular>
                <BodySmallRegular>
                    <BodySmallEmphasized>State:</BodySmallEmphasized>{' '}
                    {rowData.apexCurrentState ?? '-'}
                </BodySmallRegular>
                {rowData.apexCompletedAt != null && (
                    <BodySmallRegular>
                        <BodySmallEmphasized>Completed:</BodySmallEmphasized>{' '}
                        {new Date(rowData.apexCompletedAt).toLocaleString()}
                    </BodySmallRegular>
                )}
            </YStack>
        );
    };

    const syncteraDetailsBodyTemplate = (
        rowData: AdminICTTransferDetailDto,
    ) => {
        return (
            <YStack>
                <BodySmallRegular>
                    <BodySmallEmphasized>Pay ID:</BodySmallEmphasized>{' '}
                    {rowData.syncteraPayId ?? '-'}
                </BodySmallRegular>
                <BodySmallRegular>
                    <BodySmallEmphasized>State:</BodySmallEmphasized>{' '}
                    {rowData.syncteraPayCurrentState ?? '-'}
                </BodySmallRegular>
                <BodySmallRegular>
                    <BodySmallEmphasized>Txn UUID:</BodySmallEmphasized>{' '}
                    {rowData.syncteraTransactionUuid != null
                        ? `${rowData.syncteraTransactionUuid.substring(
                              0,
                              8,
                          )}...`
                        : '-'}
                </BodySmallRegular>
                {rowData.syncteraPayPostedAt != null && (
                    <BodySmallRegular>
                        <BodySmallEmphasized>Posted:</BodySmallEmphasized>{' '}
                        {new Date(rowData.syncteraPayPostedAt).toLocaleString()}
                    </BodySmallRegular>
                )}
            </YStack>
        );
    };

    const amountBodyTemplate = (rowData: AdminICTTransferDetailDto) => {
        const grossAmountCents =
            rowData.actualAmountCents ?? rowData.requestedAmountCents;
        const isSettled = rowData.settlementAmountCents != null;
        return (
            <YStack>
                <BodySmallRegular>
                    {centsToFormattedDollars(rowData.amountCents ?? 0)}
                </BodySmallRegular>
                {rowData.transferType === 'WITHDRAWAL' && !isSettled && (
                    <BodySmallRegular color="$gray1000">
                        Awaiting recon settlement
                    </BodySmallRegular>
                )}
                {rowData.taxAmountWithheldCents != null &&
                    grossAmountCents != null && (
                        <>
                            <BodySmallRegular color="$gray1000">
                                Gross:{' '}
                                {centsToFormattedDollars(grossAmountCents)}
                            </BodySmallRegular>
                            <BodySmallRegular color="$gray1000">
                                Tax withheld:{' '}
                                {centsToFormattedDollars(
                                    rowData.taxAmountWithheldCents,
                                )}
                            </BodySmallRegular>
                        </>
                    )}
            </YStack>
        );
    };

    const toFromBodyTemplate = (rowData: AdminICTTransferDetailDto) => {
        const isDeposit = rowData.transferType === 'DEPOSIT';
        return (
            <YStack>
                <BodySmallRegular>
                    <BodySmallEmphasized>From:</BodySmallEmphasized>{' '}
                    {isDeposit
                        ? `Bank (${rowData.bankAccountId.substring(0, 8)}...)`
                        : `Apex (${rowData.apexAccountId.substring(0, 8)}...)`}
                </BodySmallRegular>
                <BodySmallRegular>
                    <BodySmallEmphasized>To:</BodySmallEmphasized>{' '}
                    {isDeposit
                        ? `Apex (${rowData.apexAccountId.substring(0, 8)}...)`
                        : `Bank (${rowData.bankAccountId.substring(0, 8)}...)`}
                </BodySmallRegular>
            </YStack>
        );
    };

    if (isLoading) {
        return <Spinner />;
    }

    if (error != null) {
        return (
            <BodySmallEmphasized color="$red1100">
                Error loading ICT data:{' '}
                {error instanceof Error ? error.message : String(error)}
            </BodySmallEmphasized>
        );
    }

    if (data == null) {
        return (
            <BodySmallEmphasized color="$gray1000">
                No data available
            </BodySmallEmphasized>
        );
    }

    const deposits = data.transfers.filter((t) => t.transferType === 'DEPOSIT');
    const withdrawals = data.transfers.filter(
        (t) => t.transferType === 'WITHDRAWAL',
    );
    const ictSummaryRows = generateIctSummaryFromApi(deposits, withdrawals);

    return (
        <YStack gap={16}>
            <XStack
                justifyContent="space-between"
                width="100%"
                alignItems="center"
                role="banner"
            >
                <H6Emphasized>
                    ICT Daily - {format(settlementDate, 'MMMM d, yyyy')}
                </H6Emphasized>
                <XStack alignItems="center" gap={8}>
                    <BodyMediumRegular>Settlement Date:</BodyMediumRegular>
                    <Calendar
                        value={settlementDate}
                        onChange={(e) => {
                            handleDateChange(e.value as Date);
                        }}
                        showIcon
                        dateFormat="mm/dd/yy"
                        aria-label="Select settlement date"
                        maxDate={maxSelectableDate}
                        disabledDays={[0, 6]} // Disable Sundays (0) and Saturdays (6)
                    />
                </XStack>
            </XStack>

            {/* ICT Summary Table */}
            <YStack gap={8} width="100%">
                <XStack justifyContent="space-between" alignItems="center">
                    <H6Emphasized>ICT Summary</H6Emphasized>
                    <YStack alignItems="flex-end" gap={2}>
                        <H6Emphasized color="$green1000">
                            Net:{' '}
                            {centsToFormattedDollars(
                                data.summary.netCompleteAmountCents,
                            )}
                        </H6Emphasized>
                        <BodySmallRegular color="$gray1000">
                            {getWireDirectionText(data.summary.wireDirection)}
                        </BodySmallRegular>
                    </YStack>
                </XStack>
                <DataTable
                    value={ictSummaryRows}
                    size="small"
                    tableStyle={{ width: '100%' }}
                >
                    <Column field="type" header="Type" />
                    <Column
                        field="numTrx"
                        header="# Trx"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                    />
                    <Column
                        field="amountCents"
                        header="$"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                        body={(rowData: IctSummaryRow) =>
                            centsToFormattedDollars(rowData.amountCents)
                        }
                    />
                    <Column
                        field="taxAmountWithheldCents"
                        header="$ Tax Withheld"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                        body={(rowData: IctSummaryRow) =>
                            rowData.taxAmountWithheldCents == null
                                ? '-'
                                : centsToFormattedDollars(
                                      rowData.taxAmountWithheldCents,
                                  )
                        }
                    />
                    <Column
                        field="numComplete"
                        header="# Complete"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                    />
                    <Column
                        field="numPending"
                        header="# Pending"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                    />
                    <Column
                        field="numError"
                        header="# Error"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                    />
                    <Column
                        field="numCancel"
                        header="# Cancel"
                        alignHeader="right"
                        bodyStyle={{ textAlign: 'right' }}
                    />
                </DataTable>
                <BodySmallRegular color="$gray1000">
                    Withdrawal amounts are the net cash confirmed by batch
                    recon, which is what posts to the member&apos;s bank
                    account. Tax withheld is the Apex gross distribution minus
                    that net cash.
                </BodySmallRegular>
            </YStack>

            {/* ICT Transfers Detail Table */}
            <YStack gap={12}>
                <H6Emphasized>ICT Transfers</H6Emphasized>
                <BodySmallRegular>
                    Click on a row to view full transfer details
                </BodySmallRegular>

                <DataTable
                    value={data.transfers}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: '0.875rem' }}
                    emptyMessage="No transfers for this date"
                    rowClassName={(rowData: AdminICTTransferDetailDto) =>
                        rowData.transferType === 'DEPOSIT'
                            ? 'deposit-row'
                            : 'withdrawal-row'
                    }
                    style={ICT_TRANSFERS_DATATABLE_STYLE}
                    onRowClick={(e) => {
                        handleRowClick(e.data as AdminICTTransferDetailDto);
                    }}
                    selectionMode="single"
                >
                    <Column
                        field="idempotencyKey"
                        header="Idempotency Key"
                        style={{ fontSize: '0.75rem' }}
                        bodyStyle={{ wordBreak: 'break-all' }}
                        body={(rowData: AdminICTTransferDetailDto) => (
                            <BodySmallRegular>
                                {rowData.idempotencyKey}
                            </BodySmallRegular>
                        )}
                    />
                    <Column
                        field="transferType"
                        header="Type"
                        style={{ fontSize: '0.75rem' }}
                        body={(rowData: AdminICTTransferDetailDto) => (
                            <BodySmallRegular>
                                {rowData.transferType}
                            </BodySmallRegular>
                        )}
                    />
                    <Column
                        field="userId"
                        header="User"
                        style={{ fontSize: '0.75rem' }}
                        body={(rowData: AdminICTTransferDetailDto) => (
                            <BodySmallRegular>
                                {rowData.userId}
                            </BodySmallRegular>
                        )}
                    />
                    <Column
                        header="To / From"
                        style={{ fontSize: '0.75rem' }}
                        body={toFromBodyTemplate}
                    />
                    <Column
                        field="amountCents"
                        header="Net Amount"
                        style={{ fontSize: '0.75rem' }}
                        body={amountBodyTemplate}
                    />
                    <Column
                        field="status"
                        header="Status"
                        style={{ fontSize: '0.75rem' }}
                        body={statusBodyTemplate}
                    />
                    <Column
                        header="Apex Details"
                        style={{ fontSize: '0.75rem' }}
                        body={apexDetailsBodyTemplate}
                    />
                    <Column
                        header="Synctera Details"
                        style={{ fontSize: '0.75rem' }}
                        body={syncteraDetailsBodyTemplate}
                    />
                </DataTable>

                <style>{`
                    .p-datatable .deposit-row {
                        background-color: #e8f5e9 !important;
                        cursor: pointer;
                    }
                    .p-datatable .withdrawal-row {
                        background-color: #fff3e0 !important;
                        cursor: pointer;
                    }
                    .p-datatable .deposit-row:hover {
                        background-color: #c8e6c9 !important;
                    }
                    .p-datatable .withdrawal-row:hover {
                        background-color: #ffe0b2 !important;
                    }
                `}</style>
            </YStack>

            <TransferDetailsModal
                transfer={selectedTransfer}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onCancelSuccess={handleCancelSuccess}
            />

            <Divider />

            <ImOpsDailyReconPanel settlementDate={settlementDateString} />
        </YStack>
    );
};
